import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2, IconUpload } from "@tabler/icons-react"
import { useCallback, useRef } from "react"

const ACCEPTED_EXTENSIONS = ".xls,.xlsx,.doc,.docx,.pdf,.txt,.csv"

interface KnowledgebaseUploadButtonProps {
  uploadingCount: number
  onFilesSelected: (files: File[]) => void
  isDisabled?: boolean
}

export const KnowledgebaseUploadButton: React.FC<
  KnowledgebaseUploadButtonProps
> = ({ uploadingCount, onFilesSelected, isDisabled = false }) => {
  const { translate } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (!files.length) return
      onFilesSelected(files)
      e.target.value = ""
    },
    [onFilesSelected],
  )

  const isUploading = uploadingCount > 0

  return (
    <PermissionGate permission={PERMISSIONS.KB_UPLOAD}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS}
        className="hidden"
        data-testid="knowledgebase-upload-input"
        onChange={handleFileChange}
      />
      <Button
        data-agent-target="kb.upload-button"
        variant="default"
        size="sm"
        className="h-8 gap-1.5 px-3"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading || isDisabled}
      >
        {isUploading ? (
          <IconLoader2 size={14} className="animate-spin" />
        ) : (
          <IconUpload size={14} />
        )}
        <span className="text-xs">
          {translate("KNOWLEDGEBASE/HEADER_UPLOAD")}
        </span>
      </Button>
    </PermissionGate>
  )
}
